import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Repeat, ChevronLeft, Sun, Moon, Sparkles, Heart, Maximize, Minimize } from 'lucide-react'; 
import surahInfoData from '../data/surah_info.json';

const SurahDetail = ({ surah, onBack, theme, toggleTheme }) => {
  const [ayahs, setAyahs] = useState([]);
  const [pages, setPages] = useState([]); 
  
  const [loading, setLoading] = useState(true);
  const [surahInfo, setSurahInfo] = useState(null);
  const isDarkMode = theme === 'dark';
  
  const [playingAyahIndex, setPlayingAyahIndex] = useState(null); 
  const [activeWordIndex, setActiveWordIndex] = useState(-1);     
  const [activeUrduWordIndex, setActiveUrduWordIndex] = useState(-1); 
  const [playbackStatus, setPlaybackStatus] = useState('stopped'); 
  
  const [isPlayingBismillah, setIsPlayingBismillah] = useState(false);
  const [isPlayingUrdu, setIsPlayingUrdu] = useState(false); 
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isLoopSurah, setIsLoopSurah] = useState(false); 
  const [playTranslation, setPlayTranslation] = useState(true);

  // Reading Mode State
  const [isReadingMode, setIsReadingMode] = useState(false);

  const audioRef = useRef(new Audio());
  const itemRefs = useRef([]); 

  const isSajdahAyah = (surahNum, ayahNum) => {
    const sajdahs = {
       7: [206], 13: [15], 16: [50], 17: [109], 19: [58], 22: [18, 77], 
       25: [60], 27: [26], 32: [15], 38: [24], 41: [38], 53: [62], 84: [21], 96: [19]
    };
    return sajdahs[surahNum] && sajdahs[surahNum].includes(ayahNum);
  };

  const toUrduDigits = (num) => {
    if(num === undefined || num === null) return "";
    const id = ['۰','۱','۲','۳','۴','۵','۶','۷','۸','۹'];
    return num.toString().replace(/[0-9]/g, (w) => id[+w]);
  };

  const removeBismillah = (text) => {
      const variations = ["بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ", "بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ", "بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ", "بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ"];
      for (let v of variations) { if (text.startsWith(v)) return text.slice(v.length).trim(); }
      if (text.length > 50 && text.includes("ٱلرَّحِيمِ")) {
           const splitText = text.split("ٱلرَّحِيمِ");
           if (splitText.length > 1 && splitText[0].length < 40) return splitText.slice(1).join("ٱلرَّحِيمِ").trim();
      }
      return text;
  };

  useEffect(() => {
    setLoading(true);
    
    if (!surah.isCombo && !surah.ayahRange && !surah.isRoutine && surah.type !== 'juz') {
        try { 
            if (surahInfoData && surahInfoData[surah.number]) {
                setSurahInfo(surahInfoData[surah.number]); 
            } else { setSurahInfo(null); }
        } catch (e) { setSurahInfo(null); }
    } else {
        setSurahInfo(null); 
    }
    
    const fetchAyahs = async () => {
      try {
        const itemsToFetch = surah.isCombo ? surah.items : [surah];
        let allCombinedAyahs = [];

        for (let i = 0; i < itemsToFetch.length; i++) {
            const currentItem = itemsToFetch[i];
            let arabicData, urduData;

            if (currentItem.type === 'juz') {
                const [arabicRes, urduRes] = await Promise.all([
                    fetch(`https://api.alquran.cloud/v1/juz/${currentItem.number}/quran-simple`),
                    fetch(`https://api.alquran.cloud/v1/juz/${currentItem.number}/ur.jalandhry`)
                ]);
                const aData = await arabicRes.json();
                const uData = await urduRes.json();
                arabicData = aData.data.ayahs;
                urduData = uData.data.ayahs;
            } else {
                const response = await fetch(`https://api.alquran.cloud/v1/surah/${currentItem.number}/editions/quran-simple,ur.jalandhry`);
                const data = await response.json();
                arabicData = data.data[0].ayahs;
                urduData = data.data[1].ayahs;
            }
            
            let combined = arabicData.map((ayah, index) => {
              const surahNum = currentItem.type === 'juz' ? ayah.surah.number : currentItem.number;
              const surahPad = String(surahNum).padStart(3, '0');
              const ayahPad = String(ayah.numberInSurah).padStart(3, '0');
              let cleanArabicText = ayah.text;
              
              if (index === 0 && surahNum !== 1 && surahNum !== 9 && (!currentItem.ayahRange || currentItem.ayahRange[0] === 1)) {
                  cleanArabicText = removeBismillah(cleanArabicText);
              }
              
              let isNewSurah = false;
              let newSurahName = "";
              if (currentItem.type === 'juz' && ayah.numberInSurah === 1) {
                  isNewSurah = true;
                  newSurahName = ayah.surah.name;
                  if (surahNum !== 1 && surahNum !== 9) {
                      cleanArabicText = removeBismillah(cleanArabicText);
                  }
              }

              return {
                ...ayah, 
                text: cleanArabicText, 
                words: cleanArabicText.split(' ').filter(w => w.trim().length > 0), 
                urduWords: urduData[index].text.split(' ').filter(w => w.trim().length > 0),
                urduTextFull: urduData[index].text,
                arabicAudio: `https://everyayah.com/data/Alafasy_128kbps/${surahPad}${ayahPad}.mp3`,
                urduAudio: `https://everyayah.com/data/translations/urdu_shamshad_ali_khan_46kbps/${surahPad}${ayahPad}.mp3`,
                surahNum,
                isNewSurah,
                newSurahName
              };
            });

            if (currentItem.ayahRange && currentItem.ayahRange.length === 2) {
                combined = combined.filter(a => a.numberInSurah >= currentItem.ayahRange[0] && a.numberInSurah <= currentItem.ayahRange[1]);
            }

            if (combined.length > 0) {
                combined[0].isFirstOfBlock = true;
                combined[0].blockTitle = currentItem.customTitle || currentItem.ar || currentItem.name;
                combined[0].needsBismillah = currentItem.type !== 'juz' ? (currentItem.number !== 9 && (!currentItem.ayahRange || currentItem.ayahRange[0] === 1)) : false;
            }

            allCombinedAyahs = [...allCombinedAyahs, ...combined];
        }

        const groupedPages = [];
        let currentPageObj = null;
        
        allCombinedAyahs.forEach((ayah, index) => {
           ayah.globalIndex = index; 
           if (!currentPageObj || currentPageObj.pageNumber !== ayah.page) {
               if (currentPageObj) groupedPages.push(currentPageObj);
               currentPageObj = { pageNumber: ayah.page, ayahs: [] };
           }
           currentPageObj.ayahs.push(ayah);
        });
        if (currentPageObj) groupedPages.push(currentPageObj);

        setAyahs(allCombinedAyahs); 
        setPages(groupedPages);
        setLoading(false);
      } catch (err) { console.error(err); setLoading(false); }
    };
    fetchAyahs();
    return () => { audioRef.current.pause(); };
  }, [surah]);

  useEffect(() => {
      const audio = audioRef.current;
      const handleTimeUpdate = () => {
          if (audio.paused || audio.ended) return;
          const progress = audio.currentTime / audio.duration;
          if (playbackStatus === 'playing' && playingAyahIndex !== null) {
              const currentAyah = ayahs[playingAyahIndex];
              if (!isPlayingUrdu) {
                  if (currentAyah?.words.length > 0) {
                      let idx = Math.floor(progress * currentAyah.words.length);
                      if (idx >= currentAyah.words.length) idx = currentAyah.words.length - 1;
                      if (activeWordIndex !== idx) setActiveWordIndex(idx);
                  }
              } else {
                  if (currentAyah?.urduWords.length > 0) {
                      let idx = Math.floor(progress * currentAyah.urduWords.length);
                      if (idx >= currentAyah.urduWords.length) idx = currentAyah.urduWords.length - 1;
                      if (activeUrduWordIndex !== idx) setActiveUrduWordIndex(idx);
                  }
              }
          }
      };
      audio.addEventListener('timeupdate', handleTimeUpdate);
      return () => audio.removeEventListener('timeupdate', handleTimeUpdate);
  }, [playbackStatus, isPlayingUrdu, playingAyahIndex, ayahs, activeWordIndex, activeUrduWordIndex]);

  const stopAudio = () => {
    audioRef.current.pause(); audioRef.current.currentTime = 0;
    setPlayingAyahIndex(null); setActiveWordIndex(-1); setActiveUrduWordIndex(-1);
    setPlaybackStatus('stopped'); setIsPlayingBismillah(false); setIsPlayingUrdu(false);
  };

  const togglePlaySurah = (startIndex = 0) => {
    if (playbackStatus === 'playing') { audioRef.current.pause(); setPlaybackStatus('paused'); } 
    else if (playbackStatus === 'paused' && playingAyahIndex !== null) { audioRef.current.play(); setPlaybackStatus('playing'); } 
    else { 
        stopAudio(); 
        if (startIndex === 0 && ayahs[0] && ayahs[0].needsBismillah) playBismillah(0); 
        else playSequence(startIndex); 
    }
  };

  const playBismillah = (nextIndex) => {
    setIsPlayingBismillah(true); setPlaybackStatus('playing'); setPlayingAyahIndex(null); setIsPlayingUrdu(false);
    audioRef.current.src = "https://everyayah.com/data/Alafasy_128kbps/001001.mp3"; 
    audioRef.current.load(); audioRef.current.play();
    audioRef.current.onended = () => { 
        if (playTranslation) {
            playBismillahUrdu(nextIndex);
        } else {
            setIsPlayingBismillah(false); playSequence(nextIndex); 
        }
    };
  };

  const playBismillahUrdu = (nextIndex) => {
    setIsPlayingBismillah(true); setIsPlayingUrdu(true);
    audioRef.current.src = "https://everyayah.com/data/translations/urdu_shamshad_ali_khan_46kbps/001001.mp3";
    audioRef.current.load(); audioRef.current.play();
    audioRef.current.onended = () => {
        setIsPlayingBismillah(false); setIsPlayingUrdu(false); playSequence(nextIndex);
    };
  };

  const playSequence = (index) => {
    // Scrolling logic ensures the currently playing Ayah is in view
    if (itemRefs.current[index]) {
        itemRefs.current[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    playArabic(index);
  };

  const handleNextStep = (currentIndex) => {
      const nextIndex = currentIndex + 1;
      if (nextIndex < ayahs.length) { 
          if (isAutoPlay) {
              if (ayahs[nextIndex].isFirstOfBlock && ayahs[nextIndex].needsBismillah) {
                  setTimeout(() => playBismillah(nextIndex), 800);
              } else {
                  setTimeout(() => playSequence(nextIndex), 800);
              }
          } 
          else { setPlaybackStatus('stopped'); setPlayingAyahIndex(null); } 
      } 
      else { 
          if (isLoopSurah) {
             setTimeout(() => { 
                 if (ayahs[0] && ayahs[0].needsBismillah) playBismillah(0); 
                 else playSequence(0); 
             }, 1000); 
          } else {
             stopAudio(); 
          }
      }
  };

  const playArabic = (index) => {
    const ayah = ayahs[index]; if (!ayah) return;
    setPlayingAyahIndex(index); setPlaybackStatus('playing'); setIsPlayingUrdu(false); setActiveWordIndex(0); setActiveUrduWordIndex(-1); 
    audioRef.current.src = ayah.arabicAudio; audioRef.current.load(); audioRef.current.play();
    audioRef.current.onended = () => { 
        setActiveWordIndex(ayah.words.length + 1); 
        if (playTranslation && !isReadingMode) setTimeout(() => playUrdu(index), 300); 
        else handleNextStep(index); 
    };
  };

  const playUrdu = (index) => {
    const ayah = ayahs[index]; setIsPlayingUrdu(true); setActiveUrduWordIndex(0);
    audioRef.current.src = ayah.urduAudio; audioRef.current.load(); audioRef.current.play();
    audioRef.current.onended = () => { setIsPlayingUrdu(false); setActiveUrduWordIndex(ayah.urduWords.length + 1); handleNextStep(index); };
  };

  const getWordStateClass = (ayahIndex, wordIndex) => {
     if (playbackStatus === 'stopped') return "state-idle";
     if (playingAyahIndex === null) return "state-idle"; 
     if (ayahIndex < playingAyahIndex) return "state-read"; 
     if (ayahIndex === playingAyahIndex) {
         if (isPlayingUrdu) return "state-read"; 
         if (wordIndex < activeWordIndex) return "state-read"; 
         if (wordIndex === activeWordIndex) return "state-current"; 
         return "state-unread"; 
     } 
     return "state-unread";
  };

  const getUrduWordStateClass = (wordIndex) => {
      if (playingAyahIndex === null) return "state-read";
      if (!isPlayingUrdu) { if (activeUrduWordIndex > 0 && activeUrduWordIndex > wordIndex) return "state-read"; return "state-unread"; }
      if (wordIndex < activeUrduWordIndex) return "state-read"; if (wordIndex === activeUrduWordIndex) return "state-current"; return "state-unread";
  };

  if (loading) return <div className="loading-screen"><div className="premium-loader" style={{transform: 'scale(0.8)'}}></div></div>;

  return (
    <div className={`mushaf-container ${isReadingMode ? 'reading-mode-active' : ''}`}>
      
      <style>{`
        .detail-top-header { padding: 6px 10px !important; min-height: 40px !important; }
        .premium-icon-btn { padding: 4px !important; }
        
        .control-strip-container { padding: 4px 10px !important; margin: 4px 10px !important; border-radius: 15px !important; }
        .strip-btn { padding: 4px 10px !important; font-size: 0.75rem !important; }
        .strip-btn-play { width: 30px !important; height: 30px !important; }
        
        .mushaf-content-frame { padding: 8px !important; transition: all 0.3s ease; }
        
        /* ✨ PREMIUM MUSHAF BOARD (SCROLLABLE CONTINUOUS PAGE) ✨ */
        .premium-mushaf-board {
            background-color: var(--bg-card);
            border: 2px solid var(--gold-primary);
            border-radius: 15px;
            padding: 25px 15px;
            margin: 10px auto;
            max-width: 800px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.1), inset 0 0 20px rgba(212, 175, 55, 0.05);
            position: relative;
        }

        /* Inner dashed premium border */
        .premium-mushaf-board::before {
            content: '';
            position: absolute;
            top: 6px; left: 6px; right: 6px; bottom: 6px;
            border: 1px dashed rgba(212, 175, 55, 0.5);
            border-radius: 10px;
            pointer-events: none;
        }
        
        /* Quran Arabic Text */
        .quran-text-block { text-align: center; direction: rtl; position: relative; z-index: 2; }
        .ayah-word-span { font-size: 1.5rem !important; line-height: 2.2 !important; } 
        .bismillah-font { font-size: 1.5rem !important; margin: 15px 0; display: block; }
        .end-marker-style { font-size: 0.8rem !important; padding: 0 6px !important; }
        .sajdah-marker { font-size: 0.6rem !important; padding: 2px 6px !important; border-radius: 4px !important; margin-right: 4px !important; }
        
        /* Premium Page Divider */
        .premium-page-divider {
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 35px 0;
            opacity: 0.9;
        }
        .divider-line-grad-right, .divider-line-grad-left {
            flex: 1;
            height: 1px;
        }
        .divider-line-grad-right { background: linear-gradient(to right, transparent, var(--gold-primary)); }
        .divider-line-grad-left { background: linear-gradient(to left, transparent, var(--gold-primary)); }
        
        .divider-badge {
            padding: 4px 15px;
            color: var(--gold-primary);
            font-family: 'Poppins', sans-serif;
            font-size: 0.75rem;
            font-weight: 600;
            border: 1px solid var(--gold-primary);
            border-radius: 20px;
            background-color: var(--bg-card);
            margin: 0 10px;
            box-shadow: 0 2px 8px rgba(212, 175, 55, 0.2);
        }

        /* Translation Bar */
        .fixed-translation-bar { padding: 8px 10px !important; }
        .urdu-word-span { font-size: 0.85rem !important; line-height: 1.6 !important; }
        
        /* Tafseel & Azkaar */
        .tafseel-container { padding: 15px !important; margin-top: 30px !important; border-radius: 8px !important; position: relative; z-index: 2; background: rgba(212, 175, 55, 0.05); border: 1px solid rgba(212, 175, 55, 0.2); }
        .tafseel-title { font-size: 0.9rem !important; margin-bottom: 15px !important; display: block; text-align: center; color: var(--gold-primary); }
        .tafseel-label { font-size: 0.8rem !important; margin-bottom: 4px !important; }
        .tafseel-body { font-size: 0.75rem !important; line-height: 1.6 !important; margin-bottom: 12px !important; }
        
        .post-azkaar-wrapper { padding: 15px !important; border-radius: 8px !important; margin-top: 20px !important; position: relative; z-index: 2; background: rgba(212, 175, 55, 0.05); border: 1px solid rgba(212, 175, 55, 0.2); }
        .post-azkaar-title { font-size: 0.9rem !important; margin-top: 8px !important; }
        .post-azkaar-arabic { font-size: 1.2rem !important; margin-bottom: 10px !important; }
        .post-azkaar-urdu { font-size: 0.75rem !important; margin-bottom: 12px !important; }
        .azkaar-crown-icon { width: 24px !important; height: 24px !important; }

        /* ---------------------------------------------------- */
        /* ✨ READING MODE STYLES ✨ */
        /* ---------------------------------------------------- */
        .reading-mode-active {
            position: fixed !important;
            top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            z-index: 99999 !important;
            background-color: var(--bg-color, #fdfbf7) !important;
            display: flex !important;
            flex-direction: column !important;
            overflow-y: auto !important; /* ALLOW SCROLLING */
            padding: 10px !important;
            margin: 0 !important;
        }

        .reading-mode-active .mushaf-content-frame {
            flex: 1;
            padding: 0 !important;
        }

        /* Hide unnecessary elements in Reading Mode */
        .reading-mode-active .detail-top-header,
        .reading-mode-active .control-strip-container,
        .reading-mode-active .footer-wrapper {
            display: none !important; 
        }

        /* Exit Button */
        .reading-mode-exit-btn {
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 100000;
            background: rgba(212, 175, 55, 0.1);
            color: var(--gold-primary);
            border: 1px solid rgba(212, 175, 55, 0.4);
            backdrop-filter: blur(8px);
            border-radius: 50%;
            width: 45px;
            height: 45px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            box-shadow: 0 4px 10px rgba(0,0,0,0.15);
            transition: all 0.3s ease;
        }
        .reading-mode-exit-btn:hover {
            background: var(--gold-primary);
            color: #fff;
            transform: scale(1.05);
        }
      `}</style>

      {/* --- READING MODE EXIT BUTTON --- */}
      {isReadingMode && (
         <button className="reading-mode-exit-btn animate-fade-in" onClick={() => setIsReadingMode(false)} title="Exit Reading Mode">
            <Minimize size={22} strokeWidth={2} />
         </button>
      )}

      {/* --- NORMAL VIEW: Header --- */}
      {!isReadingMode && (
          <div className="detail-top-header animate-fade-in">
              <button className="premium-icon-btn" onClick={onBack} aria-label="Go Back">
                  <ChevronLeft size={20} strokeWidth={2.5} />
              </button>
              
              <div className="header-title-group">
                  {surah.isCombo ? (
                      <span className="header-ar-name" style={{fontSize: '1rem', fontFamily: "'Noto Nastaliq Urdu', serif", color: 'var(--gold-primary)'}}>{surah.comboTitle}</span>
                  ) : (
                      <>
                        <span className="header-surah-name-en" style={{fontSize: '0.8rem'}}>{surah.englishName}</span>
                        {surah.ayahRange ? (
                            <span className="header-surah-num-badge" style={{fontSize: '0.65rem', padding: '2px 6px'}}>
                                آیات {surah.ayahRange[0]}-{surah.ayahRange[1]}
                            </span>
                        ) : surah.type === 'juz' ? (
                            <span className="header-surah-num-badge" style={{fontSize: '0.65rem', padding: '2px 6px'}}>
                                پارہ ({surah.number})
                            </span>
                        ) : (
                            <span className="header-surah-num-badge" style={{fontSize: '0.7rem', width: '20px', height: '20px'}}>{surah.number}</span>
                        )}
                        <span className="header-surah-name-ar" style={{fontSize: '1rem'}}>{surah.name}</span>
                      </>
                  )}
              </div>

              <button className="premium-icon-btn" onClick={toggleTheme} aria-label="Toggle Theme">
                  {isDarkMode ? <Sun size={18} strokeWidth={2.5} /> : <Moon size={18} strokeWidth={2.5} />}
              </button>
          </div>
      )}

      {/* --- NORMAL VIEW: Control Strip --- */}
      {!isReadingMode && (
          <div className="control-strip-container animate-fade-in">
              <button onClick={() => setPlayTranslation(!playTranslation)} className={`strip-btn ${playTranslation ? 'active-mode' : ''}`}>
                 <span className="urdu-btn-text">اردو</span>
              </button>
              <div className="strip-divider" style={{height: '15px'}}></div>
              <button onClick={() => togglePlaySurah(playingAyahIndex || 0)} className="strip-btn-play">
                 {playbackStatus === 'playing' ? <Pause size={16} fill="#fff" stroke="none" /> : <Play size={16} fill="#fff" stroke="none" style={{marginLeft: '2px'}} />}
              </button>
              <div className="strip-divider" style={{height: '15px'}}></div>
              <button onClick={() => setIsLoopSurah(!isLoopSurah)} className={`strip-btn ${isLoopSurah ? 'active-mode' : ''}`}>
                 <Repeat size={16} strokeWidth={2.5} />
              </button>
              <div className="strip-divider" style={{height: '15px'}}></div>
              {/* Reading Mode Toggle Button */}
              <button onClick={() => setIsReadingMode(true)} className="strip-btn" title="Reading Mode">
                 <Maximize size={16} strokeWidth={2.5} />
              </button>
          </div>
      )}

      {/* --- MAIN QURAN CONTENT (CONTINUOUS SCROLL) --- */}
      <div className="mushaf-content-frame">
        <div className="mushaf-page-wrapper">
            
            {/* Single Continuous Premium Board */}
            <div className="premium-mushaf-board animate-fade-in">
                <div className="quran-text-block">
                    {pages.map((pageObj, pageIndex) => (
                        <React.Fragment key={`page-block-${pageObj.pageNumber}`}>
                            
                            {/* Premium Page Divider */}
                            <div className="premium-page-divider">
                                <div className="divider-line-grad-right"></div>
                                <div className="divider-badge">
                                    قرآن صفحہ {toUrduDigits(pageObj.pageNumber)} • Page {pageObj.pageNumber}
                                </div>
                                <div className="divider-line-grad-left"></div>
                            </div>

                            {/* Ayahs for this page */}
                            {pageObj.ayahs.map((ayah) => {
                                const gIndex = ayah.globalIndex;
                                return (
                                    <React.Fragment key={`ayah-${gIndex}`}>
                                        
                                        {ayah.isFirstOfBlock && (
                                            <div className="combo-block-separator">
                                                <div className="surah-end-divider" style={{margin: '8px auto 12px auto', width: '90%'}}>
                                                    <div className="divider-line"></div>
                                                    <span className="combo-block-title" style={{fontSize: '1rem'}}>{ayah.blockTitle}</span> 
                                                    <div className="divider-line"></div>
                                                </div>
                                                {ayah.needsBismillah && (
                                                    <div className="bismillah-area">
                                                    <p className={`bismillah-font ${(isPlayingBismillah && playingAyahIndex === null) ? 'state-current' : ''}`}>
                                                        بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
                                                    </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {ayah.isNewSurah && ayah.surahNum !== 1 && !ayah.isFirstOfBlock && (
                                            <div className="combo-block-separator" style={{marginTop: '15px'}}>
                                                <div className="surah-end-divider" style={{margin: '15px auto 10px auto', width: '90%'}}>
                                                    <div className="divider-line"></div>
                                                    <span className="combo-block-title" style={{fontSize: '1.2rem', fontFamily: "'Amiri', serif"}}>{ayah.newSurahName}</span> 
                                                    <div className="divider-line"></div>
                                                </div>
                                                {ayah.surahNum !== 9 && (
                                                    <div className="bismillah-area">
                                                    <p className={`bismillah-font`}>
                                                        بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
                                                    </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <span ref={el => itemRefs.current[gIndex] = el} className="ayah-container">
                                            {ayah.words.map((word, wordIndex) => (
                                                <React.Fragment key={`${gIndex}-${wordIndex}`}>
                                                    <span className={`ayah-word-span ${getWordStateClass(gIndex, wordIndex)}`} onClick={(e) => { e.stopPropagation(); togglePlaySurah(gIndex); }}>
                                                        {word}
                                                    </span>
                                                    {' '} 
                                                </React.Fragment>
                                            ))}
                                            <span className="end-marker-style">{toUrduDigits(ayah.numberInSurah)}</span>
                                            
                                            {isSajdahAyah(ayah.surahNum, ayah.numberInSurah) && (
                                                <span className="sajdah-marker" title="Sajdah e Tilawat">
                                                    ۩ سجدہ
                                                </span>
                                            )}
                                            {' '} 
                                        </span>
                                    </React.Fragment>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </div>

                {/* Surah End Ornaments & Extras (Shows at the very bottom of the board) */}
                {pages.length > 0 && (
                    <div style={{marginTop: '30px'}}>
                        <div className="surah-end-divider" style={{margin: '15px auto'}}>
                            <span className="divider-star" style={{fontSize: '0.8rem'}}>✧</span>
                            <div className="divider-line"></div>
                            <span className="divider-icon" style={{fontSize: '1.2rem'}}>❁</span> 
                            <div className="divider-line"></div>
                            <span className="divider-star" style={{fontSize: '0.8rem'}}>✧</span>
                        </div>

                        {surahInfo && !surah.isCombo && !surah.isRoutine && surah.type !== 'juz' && (
                            <div className="tafseel-container">
                                <span className="tafseel-title">- ❁ سورۃ کی تفصیل ❁ -</span>
                                <div className="tafseel-content-wrapper">
                                    {surahInfo.history && ( <div className="tafseel-item"><div className="tafseel-label"><span className="tafseel-icon">📜</span><span style={{fontFamily: "'Noto Nastaliq Urdu', serif"}}>تاریخِ نزول و پس منظر</span></div><p className="tafseel-body">{surahInfo.history}</p></div> )}
                                    {surahInfo.fazilat && ( <div className="tafseel-item"><div className="tafseel-label"><span className="tafseel-icon">💎</span><span style={{fontFamily: "'Noto Nastaliq Urdu', serif"}}>فضیلت و اہمیت</span></div><p className="tafseel-body">{surahInfo.fazilat}</p></div> )}
                                    {surahInfo.shan && ( <div className="tafseel-item"><div className="tafseel-label"><span className="tafseel-icon">🌟</span><span style={{fontFamily: "'Noto Nastaliq Urdu', serif"}}>اللہ کی شان و مرکزی مضمون</span></div><p className="tafseel-body">{surahInfo.shan}</p></div> )}
                                </div>
                            </div>
                        )}

                        {surah.postAzkaar && (
                            <div className="post-azkaar-wrapper">
                                <Sparkles className="azkaar-crown-icon" />
                                <h3 className="post-azkaar-title">{surah.postAzkaar.title}</h3>
                                <p className="post-azkaar-arabic">{surah.postAzkaar.arabic}</p>
                                <p className="post-azkaar-urdu">{surah.postAzkaar.urdu}</p>
                                <div className="post-azkaar-benefit">
                                    <Heart size={14} color="var(--gold-primary)" style={{marginTop: '2px', flexShrink: 0}} />
                                    <span style={{fontSize: '0.65rem'}}>{surah.postAzkaar.benefit}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
      </div>

      {/* --- NORMAL VIEW: Default Footer (Translation) --- */}
      {!isReadingMode && (
          <div className="footer-wrapper">
              {playTranslation && (
                <div className="fixed-translation-bar animate-fade-in" style={{ marginTop: '10px' }}>
                   <div className="trans-content-container">
                       {playingAyahIndex !== null ? (
                           ayahs[playingAyahIndex].urduWords.map((uWord, uIndex) => (
                               <span key={uIndex} className={`urdu-word-span ${getUrduWordStateClass(uIndex)}`}>{uWord}{` `}</span>
                           ))
                       ) : isPlayingBismillah ? (
                           <span className={`urdu-word-span ${isPlayingUrdu ? 'state-current' : 'state-unread'}`}>
                               شروع اللہ کے نام سے جو بڑا مہربان نہایت رحم والا ہے
                           </span>
                       ) : (
                           <span style={{color: 'var(--text-muted)', fontSize: '0.75rem', fontFamily: "'Noto Nastaliq Urdu', serif"}}>تلاوت شروع کرنے کے لیے کسی بھی آیت پر کلک کریں...</span>
                       )}
                   </div>
                </div>
              )}
          </div>
      )}
    </div>
  );
};

export default SurahDetail;